import React, { ReactElement, useEffect, useState } from "react";
import { FaPaperPlane } from "react-icons/fa";
import {
    AsyncObjectType, FormFieldType, FormFieldVisibility,
    useGetWorkflowByIdLazyQuery,
    useGetWorkflowByReferenceLazyQuery, useSaveWorkflowMutation, WorkflowStep,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import { LoadingIcon } from "../loading/Loading";
import { ReplacementGroup } from "../replacement-group";
import WorkflowDisplay, { createWorklowStep } from "./Workflow";
import {
    ASSIGNEE_FIELDS, CREATOR_FIELDS, EXECUTOR_FIELDS, REQUESTOR_FIELDS, REQUEST_FIELDS, SYSTEM_FIELDS, SOURCE_FIELDS,
} from "./WorkflowFields";

export const EMOJI_CONTEXT = {
    id: "urn:fq:form-field-group:execute-context",
    label: "Emoji Fields",
    description: "Data specific to the emoji action.",
    input: [
        {
            id: "urn:fq:form-field:messageId",
            name: "Message Id",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: "urn:fq:form-field:threadId",
            name: "Thread Id",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: "urn:fq:form-field:message",
            name: "Message",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },

        {

            id: `urn:fq:workflow-user-field:(urn:fq:form-field:messageAuthorId,name)`,
            name: "Message Author Name",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: `urn:fq:workflow-user-field:(urn:fq:form-field:messageAuthorId,firstName)`,
            name: "Message Author First Name",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: `urn:fq:workflow-user-field:(urn:fq:form-field:messageAuthorId,lastName)`,
            name: "Message Author Last Name",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: `urn:fq:workflow-user-field:(urn:fq:form-field:messageAuthorId,email)`,
            name: "Message Author Email",
            type: FormFieldType.Text,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },
        {
            id: "urn:fq:form-field:messageAuthorId",
            name: "Message Author",
            type: FormFieldType.User,
            isSystemField: true,
            visibility: FormFieldVisibility.Reusable,
        },

    ],
};

export interface WorkflowPickerProps {
    referenceId?: string;
    workflowId?: string;
    title: string;
    buttonClassName?: string;
    buttonText?: string;
    input?: ReplacementGroup[];
    children?: ReactElement[] | ReactElement | string;
    onWorkflowSaved?: (id?: string) => void;
    onOpen?: (id?: string) => void;
    shouldAddExecutor?: boolean;
    shouldAddEmojiContext?: boolean;

}
export default function WorkflowPicker(props: WorkflowPickerProps): ReactElement {
    const [shouldDisplay, setShouldDisplay] = useState(false);
    const [workflow, setWorkflow] = useState<WorkflowStep[]>();
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [loadReference, { data: referenceData, loading: isLoading }] = useGetWorkflowByReferenceLazyQuery();

    const [loadWorkflowById, { data: workflowdata, loading: isWorkflowLoading }] = useGetWorkflowByIdLazyQuery();

    const data = referenceData?.workflowReference?.workflow ?? workflowdata?.workflow;

    useEffect(() => {
        if (shouldDisplay && props.referenceId) {
            loadReference({
                variables: {
                    referenceId: props.referenceId,
                },
            });
        } else if (shouldDisplay && props.workflowId) {
            loadWorkflowById({
                variables: {
                    id: props.workflowId,
                },
            });
        }
    }, [shouldDisplay]);

    useEffect(() => {
        if (data?.id) {
            setWorkflow(data?.steps as WorkflowStep[] ?? []);
        }
    }, [data?.id]);

    const handleConfirm = async () => {
        let id: string | undefined;
        if (workflow) {
            const steps = workflow.map(createWorklowStep);
            const saved = await saveWorkflow({
                variables: {
                    id: props.workflowId ?? data?.id,
                    referenceId: props.referenceId,
                    workflow: {
                        name: "Auto generated",
                        description: `Auto generated ${props.title}`,
                        steps,
                    },
                },
            });
            id = saved.data?.saveWorkflow?.id;
        }
        props.onWorkflowSaved?.(id);

        setShouldDisplay(false);
    };

    const handleOpen = () => {
        props.onOpen?.();
        setShouldDisplay(true);
    };
    return (
        <>
            <button
                className={`btn btn-none ${props.buttonClassName}`}
                type="button"
                onClick={handleOpen}
            >
                <FaPaperPlane className={props.buttonClassName} />
                {props.buttonText && ` ${props.buttonText}`}
            </button>
            <Confirm
                shouldShow={shouldDisplay}
                onHide={() => setShouldDisplay(false)}
                size="xl"
                dialogClassName="full-size-container"
                confirmText="Save"
                onConfirm={handleConfirm}
            >
                <div>
                    <h4>
                        {props.title}
                    </h4>
                    {props.children && (
                        <div className="border p-2 my-3">
                            {props.children}
                        </div>
                    )}

                    {(isLoading || isWorkflowLoading) ? <LoadingIcon /> : (
                        <div className="mt-5">
                            <WorkflowDisplay
                                value={workflow}
                                onChange={updated => setWorkflow(updated)}
                                input={[
                                    (props.shouldAddExecutor ? EXECUTOR_FIELDS : undefined),
                                    (props.shouldAddEmojiContext ? EMOJI_CONTEXT : undefined),
                                    REQUEST_FIELDS, REQUESTOR_FIELDS, SYSTEM_FIELDS,
                                    CREATOR_FIELDS, ASSIGNEE_FIELDS,
                                    SOURCE_FIELDS, ...(props.input ?? []),
                                ].filterNull()}
                            />
                        </div>
                    )}

                </div>

            </Confirm>

        </>
    );
}

WorkflowPicker.defaultProps = {
    buttonClassName: "",
    buttonText: "",
    referenceId: undefined,
    workflowId: undefined,
    onWorkflowSaved: undefined,
    children: undefined,
    onOpen: undefined,
    input: [],
    shouldAddExecutor: false,
    shouldAddEmojiContext: false,
};
